import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { BookMarked, Users, FileText, CheckCircle2, Clock } from "lucide-react";

export default function HelmMyBook() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/agent/my-book").then(({ data }) => setD(data))
      .catch(() => setErr("Could not load your book."));
  }, []);

  if (err) return <div className="p-6 sm:p-10 max-w-4xl mx-auto"><div className="rounded-xl border-2 border-[#F3D6D6] bg-[#FDF3F3] p-8 text-center text-[#991B1B] font-semibold" data-testid="my-book-error">{err}</div></div>;
  if (!d) return <div className="p-6 sm:p-10 max-w-4xl mx-auto"><Skeleton className="h-80 w-full rounded-xl" /></div>;

  const t = d.totals;
  return (
    <div className="p-6 sm:p-10 max-w-4xl mx-auto space-y-6" data-testid="my-book">
      <div>
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[#D4AF37]"><BookMarked className="w-4 h-4" />My Book</div>
        <h1 className="font-display font-bold text-3xl lg:text-4xl text-[#0B192C] tracking-tight mt-1" data-testid="my-book-title">Business I wrote</h1>
        <p className="text-muted-foreground mt-1.5">{d.note}</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label="Client households" value={t.distinct_client_households} icon={Users} />
        <Stat label="Policies" value={t.policies} icon={FileText} />
        <Stat label="Existing book" value={t.existing_book} icon={BookMarked} tone="text-[#1E3E62]" />
        <Stat label="New production" value={t.new_production} icon={CheckCircle2} tone="text-[#166534]" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Stat label="Submitted (not yet effective)" value={t.submitted} icon={Clock} tone="text-[#92400E]" />
        <Stat label="Effective / active" value={t.effective} icon={CheckCircle2} tone="text-[#166534]" />
      </div>

      <div className="rounded-xl border-2 border-[#E5E5E0] bg-white" data-testid="my-book-list">
        <div className="px-4 py-3 border-b font-bold text-[#0B192C]">Attested policies</div>
        {d.items.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#6B7280]" data-testid="my-book-empty">No attested policies yet. On a household's Coverage tab, use "Existing client / business I wrote" to attest a policy.</div>
        ) : (
          <div className="divide-y">
            {d.items.map((it) => (
              <div key={it.coverage_id} className="flex items-center gap-3 p-3" data-testid={`my-book-item-${it.coverage_id}`}>
                <div className="flex-1 min-w-0">
                  <Link to={`/agent/households/${it.household_id}?tab=coverage`} className="text-sm font-semibold text-[#0B192C] hover:underline truncate block">{it.household_name}{it.person ? ` · ${it.person}` : ""}</Link>
                  <p className="text-xs text-[#6B7280] truncate">{[it.carrier, it.plan_name].filter(Boolean).join(" · ") || "Policy"}{it.writing_agency ? ` · ${it.writing_agency}` : ""}</p>
                </div>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${it.book_class === "new_production" ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#EEF2F6] text-[#1E3E62]"}`}>{it.book_class === "new_production" ? "New" : "Existing"}</span>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${it.production_state === "effective" ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FEF3C7] text-[#92400E]"}`}>{it.production_state === "effective" ? "Effective" : "Submitted"}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone = "text-[#0B192C]" }) {
  return (
    <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-4" data-testid={`my-book-stat-${label}`}>
      <div className="flex items-center gap-2 text-[#9CA3AF] mb-1"><Icon className="w-4 h-4" /><span className="text-[11px] font-semibold uppercase tracking-wide">{label}</span></div>
      <p className={`text-2xl font-bold ${tone}`}>{value}</p>
    </div>
  );
}
